import React, { useState, useEffect, useRef } from 'react';
import API from '../../services/api.js';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai';

const EnhancedSelectField = ({ 
  name, 
  value, 
  handleChange, 
  handleFocus, 
  handleBlur, 
  focusedField, 
  options, 
  placeholder, 
  applicationDetails,
  isLoading = false,
  isDropdownLoading = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Update the filteredOptions logic to show loading state
  const getOptionsToShow = () => {
    if (isLoading) {
      return [];
    }
    return options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredOptions = getOptionsToShow();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectOption = (optionValue, optionLabel) => {
    handleChange({ target: { name, value: optionValue } });
    setIsOpen(false);
    setSearchTerm('');
  };

  const selectedOption = options.find(option => option.value === value);

  return (
    <div className="w-full transform transition-all duration-500 hover:scale-102 relative z-[1] dropdown-container" ref={dropdownRef}>
      <div className="relative group">
        {/* Main Select Button */}
        <button
          type="button"
          onClick={() => {
            setIsOpen(!isOpen);
            handleFocus(name);
          }}
          onBlur={handleBlur}
          className={`w-full bg-[#232325]/90 backdrop-blur-sm border-2 ${
            focusedField === name || isOpen
              ? 'border-blue-500 shadow-lg shadow-blue-500/30 scale-105'
              : 'border-[#232325] hover:border-blue-400/50 hover:shadow-md hover:shadow-blue-500/10'
          } px-6 py-4 rounded-2xl text-left text-sm focus:outline-none transition-all duration-300 transform hover:translate-y-[-2px] cursor-pointer flex items-center justify-between`}
        >
          <span className={selectedOption ? 'text-white' : 'text-gray-500'}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <svg 
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown Menu - Updated z-index and positioning */}
        {isOpen && (
          <div 
            className="absolute top-full left-0 right-0 mt-2 bg-[#232325]/95 backdrop-blur-xl border border-[#232325] rounded-2xl shadow-2xl shadow-black/50 max-h-80 overflow-hidden animate-in slide-in-from-top-2 duration-200 dropdown-menu"
            style={{ position: 'absolute', zIndex: 10000 }}
          >
            {/* Search Input */}
            <div className="p-3 border-b border-gray-700/50">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search applications..."
                  className="w-full pl-10 pr-4 py-2 bg-[#1a1a1c] border border-gray-600/30 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-colors"
                  autoFocus
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Options List - Updated with loading states */}
            <div className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-track-gray-800 scrollbar-thumb-gray-600">
              {isLoading ? (
                // Loading state for dropdown
                <div className="px-4 py-8 text-center text-gray-400">
                  <div className="flex flex-col items-center space-y-3">
                    <div className="relative">
                      <svg className="animate-spin w-8 h-8 text-blue-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <p className="text-sm">Loading applications...</p>
                    <p className="text-xs opacity-75">Please wait a moment</p>
                  </div>
                </div>
              ) : filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => {
                  const isSelected = option.value === value;
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSelectOption(option.value, option.label)}
                      className={`w-full px-4 py-3 text-left hover:bg-[#1a1a1c] transition-colors duration-150 border-b border-gray-700/30 last:border-b-0 ${
                        isSelected ? 'bg-blue-600/20 text-blue-300' : 'text-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              isSelected ? 'bg-blue-400' : 'bg-gray-500'
                            }`} />
                            <div className="min-w-0 flex-1">
                              <p className="font-medium truncate text-sm">
                                {option.label.split(' (')[0]}
                              </p>
                              <p className="text-xs text-gray-400 truncate">
                                {option.label.includes('(') ? option.label.split('(')[1]?.replace(')', '') : 'No user data'}
                              </p>
                            </div>
                          </div>
                        </div>
                        {isSelected && (
                          <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center text-gray-400">
                  <svg className="mx-auto h-8 w-8 mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm">No applications found</p>
                  <p className="text-xs">Try adjusting your search</p>
                </div>
              )}
            </div>

            {/* Footer with count - Updated to hide during loading */}
            {!isLoading && filteredOptions.length > 0 && (
              <div className="px-4 py-2 bg-[#1a1a1c] border-t border-gray-700/50">
                <p className="text-xs text-gray-400 text-center">
                  {filteredOptions.length} of {options.length} application(s)
                </p>
              </div>
            )}
          </div>
        )}

        {/* Focus/Hover Effects */}
        <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/20 via-blue-400/20 to-blue-300/20 opacity-0 ${
          focusedField === name || isOpen ? 'opacity-100' : 'group-hover:opacity-50'
        } transition-opacity duration-300 pointer-events-none blur-xl`} />
      </div>
    </div>
  );
};

const FloatingParticle = ({ delay, duration, initialX, initialY }) => (
  <div
    className="fixed w-1 h-1 bg-blue-400 rounded-full opacity-30 z-0" // Changed to fixed
    style={{
      left: `${initialX}vw`, // Use viewport units
      top: `${initialY}vh`,
      animation: `float ${duration}s ease-in-out infinite`,
      animationDelay: `${delay}s`
    }}
  />
);

const InputField = ({ type, name, placeholder, value, handleChange, handleFocus, handleBlur, focusedField, index }) => {
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div 
      className="w-full transform transition-all duration-500 hover:scale-102"
      style={{
        animation: `slideInUp 0.6s ease-out ${index * 0.1}s both`
      }}
    >
      <div className="relative group">
        <input
          type={type === 'password' && showPassword ? 'text' : type}
          name={name}
          value={value}
          onChange={handleChange}
          onFocus={() => handleFocus(name)}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`w-full bg-[#232325]/90 backdrop-blur-sm border-2 ${
            focusedField === name
              ? 'border-blue-500 shadow-lg shadow-blue-500/30 scale-105'
              : 'border-[#232325] hover:border-blue-400/50 hover:shadow-md hover:shadow-blue-500/10'
          } px-6 py-4 rounded-2xl placeholder:text-gray-500 text-white text-sm focus:outline-none transition-all duration-300 transform hover:translate-y-[-2px]`}
        />
        {type === 'password' && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5">
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="text-gray-500 focus:outline-none focus:text-white"
            >
              {showPassword ? (
                <AiFillEyeInvisible className="h-5 w-5" />
              ) : (
                <AiFillEye className="h-5 w-5" />
              )}
            </button>
          </div>
        )}
        <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/20 via-blue-400/20 to-blue-300/20 opacity-0 ${
          focusedField === name ? 'opacity-100' : 'group-hover:opacity-50'
        } transition-opacity duration-300 pointer-events-none blur-xl`} />
      </div>
    </div>
  );
};



const UserSection = ({ title, users, onAddUser, onRemoveUser, onUserChange, focusedField, handleFocus, handleBlur, userType, maxUsers, currentUserCount = 0 }) => {
  const remainingSlots = maxUsers - currentUserCount;
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold bg-gradient-to-r from-white via-blue-200 to-purple-200 bg-clip-text text-transparent">
          {title}
        </h3>
        <div className="flex items-center space-x-4">
          <span className="text-gray-400 text-sm">
            {currentUserCount > 0 
              ? `${users.length}/${remainingSlots} new users (${currentUserCount} existing)`
              : `${users.length}/${maxUsers} users`
            }
          </span>
          <button
            onClick={onAddUser}
            disabled={users.length >= remainingSlots}
            className={`group relative flex items-center space-x-2 px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 transform ${
              users.length >= remainingSlots
                ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed border border-gray-600' 
                : 'bg-blue-600/10 text-blue-400 border border-blue-500/30 hover:bg-blue-600/20 hover:border-blue-400 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/20'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Add {userType}</span>
            {users.length < remainingSlots && (
              <div className="absolute inset-0 rounded-xl bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            )}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {users.map((user, index) => (
          <div key={index} className="relative group">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                type="email"
                name={`${userType}_username_${index}`}
                value={user.username}
                placeholder={`${userType} Email ${index + 1}`}
                handleChange={(e) => onUserChange(index, 'username', e.target.value)}
                handleFocus={handleFocus}
                handleBlur={handleBlur}
                focusedField={focusedField}
                index={index}
              />
              <InputField
                type="password"
                name={`${userType}_password_${index}`}
                value={user.password}
                placeholder={`${userType} Password ${index + 1}`}
                handleChange={(e) => onUserChange(index, 'password', e.target.value)}
                handleFocus={handleFocus}
                handleBlur={handleBlur}
                focusedField={focusedField}
                index={index}
              />
            </div>
            
            {users.length > 1 && (
              <button
                onClick={() => onRemoveUser(index)}
                className="absolute -top-2 -right-2 w-8 h-8 bg-red-600/20 border border-red-500/30 text-red-400 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-red-600/30 hover:border-red-400 hover:scale-110"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, children, icon }) => (
  <button
    onClick={onClick}
    className={`relative flex items-center space-x-3 px-8 py-4 rounded-xl font-medium text-sm transition-all duration-300 transform ${
      active
        ? 'bg-blue-600/20 text-blue-300 border border-blue-500/40 shadow-lg shadow-blue-500/20 scale-105'
        : 'bg-gray-800/30 text-gray-400 border border-gray-700/50 hover:bg-gray-700/40 hover:text-gray-300 hover:border-gray-600 hover:scale-105'
    }`}
  >
    {icon}
    <span>{children}</span>
    {active && (
      <div className="absolute inset-0 bg-blue-500/5 rounded-xl animate-pulse" />
    )}
  </button>
);

const Onboarding = () => {
  const [activeTab, setActiveTab] = useState('create');
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState('');
  const [applicationDetails, setApplicationDetails] = useState(null);
  // Create tab state
  const [formData, setFormData] = useState({
    company: '',
  });
  const [clientUsers, setClientUsers] = useState([
    { username: '', password: '' }
  ]);
  const [viewerUsers, setViewerUsers] = useState([
    { username: '', password: '' }
  ]);

  // Update tab state
  const [updateClientUsers, setUpdateClientUsers] = useState([
    { username: '', password: '' }
  ]);
  const [updateViewerUsers, setUpdateViewerUsers] = useState([
    { username: '', password: '' }
  ]);

  const [focusedField, setFocusedField] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [isDropdownLoading, setIsDropdownLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [mounted, setMounted] = useState(false);
  const [particles, setParticles] = useState([]);
  
  // Feature selection state for create tab
  const [selectedFeatures, setSelectedFeatures] = useState({
    llmRedTeaming: false,
    imageAIRedTeaming: false,
    guardRails: false,
    staticanalysis: false,
    prompthardening: false,
  });

  // Feature selection state for update tab
  const [updateSelectedFeatures, setUpdateSelectedFeatures] = useState({
    llmRedTeaming: false,
    imageAIRedTeaming: false,
    guardRails: false,
    staticanalysis: false,
    prompthardening: false,
  });

  const MAX_USERS = 5;

  // Function to map API feature names to internal feature keys
  const mapApiFeaturesToInternal = (apiFeatures) => {
    const featureMapping = {
      'LLM Testing': 'llmRedTeaming',
      'Image Model Testing': 'imageAIRedTeaming',
      'GuardRails': 'guardRails',
      'Static Code Analysis': 'staticanalysis',
      'System Prompt Hardening': 'prompthardening',
    };

    const mappedFeatures = {
      llmRedTeaming: false,
      imageAIRedTeaming: false,
      guardRails: false,
      staticanalysis: false,
      prompthardening: false,
    };

    if (apiFeatures && Array.isArray(apiFeatures)) {
      apiFeatures.forEach(feature => {
        const internalKey = featureMapping[feature];
        if (internalKey) {
          mappedFeatures[internalKey] = true;
        }
      });
    }

    return mappedFeatures;
  };


  useEffect(() => {
    setMounted(true);
    
    // Generate floating particles
    const generateParticles = () => {
      const newParticles = [];
      for (let i = 0; i < 15; i++) {
        newParticles.push({
          id: i,
          delay: Math.random() * 3,
          duration: 3 + Math.random() * 4,
          initialX: Math.random() * 100,
          initialY: Math.random() * 100
        });
      }
      setParticles(newParticles);
    };

    generateParticles();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

const handleApplicationChange = async (e) => {
  const appId = e.target.value;
  setSelectedApplication(appId);
  setIsDropdownLoading(true);
  
  try {
    // Find the selected application in the applications array
    const selectedApp = applications.find(app => app.tenant_name === appId);
    setApplicationDetails(selectedApp || null);

    // Set the features for the selected application
    if (selectedApp && selectedApp.features) {
      const mappedFeatures = mapApiFeaturesToInternal(selectedApp.features);
      setUpdateSelectedFeatures(mappedFeatures);
    } else {
      // Reset features if no application selected
      setUpdateSelectedFeatures({
        llmRedTeaming: false,
        imageAIRedTeaming: false,
        guardRails: false,
        staticanalysis: false,
        prompthardening: false,
      });
    }
  } catch (error) {
    console.error('Error processing application selection:', error);
  } finally {
    setIsDropdownLoading(false);
  }
};


  const handleClientUserChange = (index, field, value) => {
    const updatedUsers = [...clientUsers];
    updatedUsers[index][field] = value;
    setClientUsers(updatedUsers);
  };

  const handleViewerUserChange = (index, field, value) => {
    const updatedUsers = [...viewerUsers];
    updatedUsers[index][field] = value;
    setViewerUsers(updatedUsers);
  };

  const addClientUser = () => {
    if (clientUsers.length < MAX_USERS) {
      setClientUsers([...clientUsers, { username: '', password: '' }]);
    }
  };

  const addViewerUser = () => {
    if (viewerUsers.length < MAX_USERS) {
      setViewerUsers([...viewerUsers, { username: '', password: '' }]);
    }
  };

  const removeClientUser = (index) => {
    if (clientUsers.length > 1) {
      const updatedUsers = clientUsers.filter((_, i) => i !== index);
      setClientUsers(updatedUsers);
    }
  };

  const removeViewerUser = (index) => {
    if (viewerUsers.length > 1) {
      const updatedUsers = viewerUsers.filter((_, i) => i !== index);
      setViewerUsers(updatedUsers);
    }
  };

  const handleUpdateClientUserChange = (index, field, value) => {
    const updatedUsers = [...updateClientUsers];
    updatedUsers[index][field] = value;
    setUpdateClientUsers(updatedUsers);
  };

  const handleUpdateViewerUserChange = (index, field, value) => {
    const updatedUsers = [...updateViewerUsers];
    updatedUsers[index][field] = value;
    setUpdateViewerUsers(updatedUsers);
  };

  const addUpdateClientUser = () => {
    const currentCount = applicationDetails?.admin_count || 0;
    const remainingSlots = MAX_USERS - currentCount;
    if (updateClientUsers.length < remainingSlots) {
      setUpdateClientUsers([...updateClientUsers, { username: '', password: '' }]);
    }
  };

  const addUpdateViewerUser = () => {
    const currentCount = applicationDetails?.viewer_count || 0;
    const remainingSlots = MAX_USERS - currentCount;
    if (updateViewerUsers.length < remainingSlots) {
      setUpdateViewerUsers([...updateViewerUsers, { username: '', password: '' }]);
    }
  };

  const removeUpdateClientUser = (index) => {
    if (updateClientUsers.length > 1) {
      const updatedUsers = updateClientUsers.filter((_, i) => i !== index);
      setUpdateClientUsers(updatedUsers);
    }
  };

  const removeUpdateViewerUser = (index) => {
    if (updateViewerUsers.length > 1) {
      const updatedUsers = updateViewerUsers.filter((_, i) => i !== index);
      setUpdateViewerUsers(updatedUsers);
    }
  };

  const FeatureToggle = ({ name, label, isActive, onChange, description }) => (
    <div 
      className={`p-6 rounded-2xl border-2 transition-all duration-300 transform hover:scale-[1.02] cursor-pointer ${
        isActive 
          ? 'bg-[#232325]/80 border-blue-500 shadow-lg shadow-blue-500/20' 
          : 'bg-[#232325]/40 border-[#232325] hover:border-blue-400/30 hover:shadow-md hover:shadow-blue-500/10'
      }`}
      onClick={() => onChange(name, !isActive)}
    >
      <div className="flex items-start space-x-4">
        <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
          isActive 
            ? 'bg-blue-500 border-blue-500' 
            : 'bg-transparent border-gray-500'
        }`}>
          {isActive && (
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        <div className="flex-1">
          <h4 className="text-lg font-semibold text-white mb-1">{label}</h4>
          <p className="text-gray-400 text-sm">{description}</p>
        </div>
      </div>
    </div>
  );

  const handleSubmit = async () => {
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      if (activeTab === 'create') {
        // Filter out empty users before sending
        const validClientUsers = clientUsers.filter(user => 
          user.username.trim() !== '' && user.password.trim() !== ''
        );
        const validViewerUsers = viewerUsers.filter(user => 
          user.username.trim() !== '' && user.password.trim() !== ''
        );

        const adminNames = validClientUsers.map(user => user.username);
        const adminPasswords = validClientUsers.map(user => user.password);
        const viewerNames = validViewerUsers.map(user => user.username);
        const viewerPasswords = validViewerUsers.map(user => user.password);

        const formDataToSend = new FormData();
        formDataToSend.append('company', formData.company);
        formDataToSend.append(`adminNames`, JSON.stringify(adminNames));
        formDataToSend.append(`adminPasswords`, JSON.stringify(adminPasswords));
        formDataToSend.append(`viewerNames`, JSON.stringify(viewerNames));
        formDataToSend.append(`viewerPasswords`, JSON.stringify(viewerPasswords));
        formDataToSend.append('clientCount', validClientUsers.length.toString());
        formDataToSend.append('viewerCount', validViewerUsers.length.toString());
        formDataToSend.append('features', JSON.stringify(selectedFeatures));

        const response = await API.post(`${process.env.REACT_APP_DJANGO_API_URL}/add-tenant/`, formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        const data = response.data;

        if (response.ok && data.status === 'success') {
          setMessage({ 
            type: 'success', 
            text: `Tenant and ${validClientUsers.length} admin(s) + ${validViewerUsers.length} viewer(s) created successfully!` 
          });
          setFormData({ company: '' });
          setClientUsers([{ username: '', password: '' }]);
          setViewerUsers([{ username: '', password: '' }]);
          setSelectedFeatures({
            llmRedTeaming: false,
            imageAIRedTeaming: false,
            guardRails: false,
            staticanalysis: false,
            prompthardening: false,
          });
        } else {
          setMessage({ 
            type: 'error', 
            text: data.message || data.Error || 'Failed to create tenant and users' 
          });
        }
      } else {
        // Update users and features logic
        if (!selectedApplication) {
          setMessage({ type: 'error', text: 'Please select an application first' });
          return;
        }

        // Filter out empty users before sending
        const validUpdateClientUsers = updateClientUsers.filter(user => 
          user.username.trim() !== '' && user.password.trim() !== ''
        );
        const validUpdateViewerUsers = updateViewerUsers.filter(user => 
          user.username.trim() !== '' && user.password.trim() !== ''
        );

        const adminNames = validUpdateClientUsers.map(user => user.username);
        const adminPasswords = validUpdateClientUsers.map(user => user.password);
        const viewerNames = validUpdateViewerUsers.map(user => user.username);
        const viewerPasswords = validUpdateViewerUsers.map(user => user.password);

        const updateData = new FormData();
        updateData.append('company', selectedApplication);
        updateData.append('adminNames', JSON.stringify(adminNames));
        updateData.append('adminPasswords', JSON.stringify(adminPasswords));
        updateData.append('viewerNames', JSON.stringify(viewerNames));
        updateData.append('viewerPasswords', JSON.stringify(viewerPasswords));
        updateData.append('features', JSON.stringify(updateSelectedFeatures)); // Add features to update

        const response = await API.post(`${process.env.REACT_APP_DJANGO_API_URL}/add-users/`, updateData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        const data = response.data;

        if (data.status === 'success') {
          const totalUsersAdded = validUpdateClientUsers.length + validUpdateViewerUsers.length;
          
          if (totalUsersAdded === 0) {
            setMessage({ 
              type: 'success', 
              text: 'Tenant Updated Successfully!' 
            });
          } else {
            setMessage({ 
              type: 'success', 
              text: `Added ${validUpdateClientUsers.length} admin(s) and ${validUpdateViewerUsers.length} viewer(s) successfully!` 
            });
          }
          
          setUpdateClientUsers([{ username: '', password: '' }]);
          setUpdateViewerUsers([{ username: '', password: '' }]);
          // Refresh the applications list to get updated features
          await handleUpdateTabClick();
        } else {
          setMessage({ 
            type: 'error', 
            text: data.message || data.Error || 'Failed to update users' 
          });
        }
      } 
    }
    catch (error) {
        setMessage({ 
          type: 'error', 
          text: 'Network error. Please check your connection and try again.' 
        });
        console.error('Error:', error);
      } finally {
        setIsLoading(false);
      }
    };

  const handleFocus = (fieldName) => {
    setFocusedField(fieldName);
  };

  const handleBlur = () => {
    setFocusedField('');
  };

  const applicationOptions = applications.map(app => ({
    value: app.tenant_name,
    label: `${app.tenant_name} (${app.admin_count || 0} admins, ${app.viewer_count || 0} viewers)`
  }));

  const renderCreateTab = () => (
    <div className="space-y-12">
      <InputField
        type="text"
        name="company"
        value={formData.company}
        placeholder="Company Name"
        handleChange={handleChange}
        handleFocus={handleFocus}
        handleBlur={handleBlur}
        focusedField={focusedField}
        index={0}
      />

      <UserSection
        title="Admin Users"
        users={clientUsers}
        onAddUser={addClientUser}
        onRemoveUser={removeClientUser}
        onUserChange={handleClientUserChange}
        focusedField={focusedField}
        handleFocus={handleFocus}
        handleBlur={handleBlur}
        userType="Admin"
        maxUsers={MAX_USERS}
      />

      <UserSection
        title="Viewer Users"
        users={viewerUsers}
        onAddUser={addViewerUser}
        onRemoveUser={removeViewerUser}
        onUserChange={handleViewerUserChange}
        focusedField={focusedField}
        handleFocus={handleFocus}
        handleBlur={handleBlur}
        userType="Viewer"
        maxUsers={MAX_USERS}
      />

      <div className="space-y-6">
        <h3 className="text-2xl font-bold bg-gradient-to-r from-white via-blue-200 to-blue-100 bg-clip-text text-transparent">
          Feature Access
        </h3>
        <p className="text-gray-400 text-sm">
          Select which features this tenant should have access to
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <FeatureToggle
            name="llmRedTeaming"
            label="LLM Red Teaming"
            isActive={selectedFeatures.llmRedTeaming}
            onChange={(name, value) => setSelectedFeatures({...selectedFeatures, [name]: value})}
            description="Advanced testing for language model vulnerabilities"
          />
          
          <FeatureToggle
            name="imageAIRedTeaming"
            label="Image AI Red Teaming"
            isActive={selectedFeatures.imageAIRedTeaming}
            onChange={(name, value) => setSelectedFeatures({...selectedFeatures, [name]: value})}
            description="Security testing for image generation models"
          />
          
          <FeatureToggle
            name="guardRails"
            label="GuardRails"
            isActive={selectedFeatures.guardRails}
            onChange={(name, value) => setSelectedFeatures({...selectedFeatures, [name]: value})}
            description="Content moderation and safety controls"
          />
                  
          <FeatureToggle
            name="staticanalysis"
            label="Static Analysis"
            isActive={selectedFeatures.staticanalysis}
            onChange={(name, value) => setSelectedFeatures({...selectedFeatures, [name]: value})}
            description="Static code analysis for security vulnerabilities"
          />
                  
          <FeatureToggle
            name="prompthardening"
            label="System Prompt Hardening"
            isActive={selectedFeatures.prompthardening}
            onChange={(name, value) => setSelectedFeatures({...selectedFeatures, [name]: value})}
            description="Content moderation and safety controls"
          />
        </div>
      </div>

      <div className="flex justify-center pt-10">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
          className={`group relative overflow-hidden ${
            isLoading 
              ? 'bg-blue-800/30 cursor-not-allowed scale-95 border border-blue-700/40' 
              : 'bg-blue-600/10 border border-blue-500/30 hover:bg-blue-600/20 hover:border-blue-400/50 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/20 active:scale-100'
          } text-blue-300 px-8 py-4 rounded-xl font-medium text-lg transition-all duration-300 transform focus:outline-none focus:ring-2 focus:ring-blue-500/40`}
        >
          <span className="relative z-10 flex items-center space-x-3">
            <span className="transform transition-transform group-hover:scale-105">
              {isLoading ? 'Creating Accounts...' : 'Complete Setup'}
            </span>
            {!isLoading ? (
              <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            ) : (
              <div className="relative">
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
          </span>
        </button>
      </div>
    </div>
  );

const renderUpdateTab = () => (
  <div className="space-y-12">
    <div className="space-y-6">
      <h3 className="text-2xl font-bold bg-gradient-to-r from-white via-blue-200 to-purple-200 bg-clip-text text-transparent">
        Select Application
      </h3>
      {/* Use the enhanced dropdown with loading states */}
      <EnhancedSelectField
        name="application"
        value={selectedApplication}
        handleChange={handleApplicationChange}
        handleFocus={handleFocus}
        handleBlur={handleBlur}
        focusedField={focusedField}
        options={applicationOptions}
        placeholder="Choose an application to update"
        applicationDetails={applicationDetails}
        isLoading={isLoadingTenants}
        isDropdownLoading={isDropdownLoading}
      />
    </div>

    {isDropdownLoading && selectedApplication && (
      <div className="bg-[#232325]/40 backdrop-blur-sm border border-[#232325] rounded-2xl p-6">
        <div className="flex items-center justify-center space-x-3">
          <svg className="animate-spin w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-400">Loading application details...</span>
        </div>
      </div>
    )}

    {applicationDetails && !isDropdownLoading && (
      <div className="bg-[#232325]/40 backdrop-blur-sm border border-[#232325] rounded-2xl p-6 application-status">
        <h4 className="text-lg font-semibold text-white mb-4">Current Application Status</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <span className="text-gray-400 text-sm">Company:</span>
            <p className="text-white font-medium">{applicationDetails.tenant_name}</p>
          </div>
          <div className="space-y-2">
            <span className="text-gray-400 text-sm">Current Users:</span>
            <p className="text-white font-medium">
              {applicationDetails.admin_count || 0} Admin(s), {applicationDetails.viewer_count || 0} Viewer(s)
            </p>
          </div>
        </div>
      </div>
    )}

    {selectedApplication && applicationDetails && !isDropdownLoading && (
      <>
        <UserSection
          title="Add Admin Users"
          users={updateClientUsers}
          onAddUser={addUpdateClientUser}
          onRemoveUser={removeUpdateClientUser}
          onUserChange={handleUpdateClientUserChange}
          focusedField={focusedField}
          handleFocus={handleFocus}
          handleBlur={handleBlur}
          userType="Admin"
          maxUsers={MAX_USERS}
          currentUserCount={applicationDetails.admin_count || 0}
        />

        <UserSection
          title="Add Viewer Users"
          users={updateViewerUsers}
          onAddUser={addUpdateViewerUser}
          onRemoveUser={removeUpdateViewerUser}
          onUserChange={handleUpdateViewerUserChange}
          focusedField={focusedField}
          handleFocus={handleFocus}
          handleBlur={handleBlur}
          userType="Viewer"
          maxUsers={MAX_USERS}
          currentUserCount={applicationDetails.viewer_count || 0}
        />

        {/* Feature Access Section for Update Tab */}
        <div className="space-y-6">
          <h3 className="text-2xl font-bold bg-gradient-to-r from-white via-blue-200 to-blue-100 bg-clip-text text-transparent">
            Update Feature Access
          </h3>
          <p className="text-gray-400 text-sm">
            Modify which features this tenant should have access to
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FeatureToggle
              name="llmRedTeaming"
              label="LLM Red Teaming"
              isActive={updateSelectedFeatures.llmRedTeaming}
              onChange={(name, value) => setUpdateSelectedFeatures({...updateSelectedFeatures, [name]: value})}
              description="Advanced testing for language model vulnerabilities"
            />
            
            <FeatureToggle
              name="imageAIRedTeaming"
              label="Image AI Red Teaming"
              isActive={updateSelectedFeatures.imageAIRedTeaming}
              onChange={(name, value) => setUpdateSelectedFeatures({...updateSelectedFeatures, [name]: value})}
              description="Security testing for image generation models"
            />
            
            <FeatureToggle
              name="guardRails"
              label="GuardRails"
              isActive={updateSelectedFeatures.guardRails}
              onChange={(name, value) => setUpdateSelectedFeatures({...updateSelectedFeatures, [name]: value})}
              description="Content moderation and safety controls"
            />
                    
            <FeatureToggle
              name="staticanalysis"
              label="Static Analysis"
              isActive={updateSelectedFeatures.staticanalysis}
              onChange={(name, value) => setUpdateSelectedFeatures({...updateSelectedFeatures, [name]: value})}
              description="Static code analysis for security vulnerabilities"
            />
                    
            <FeatureToggle
              name="prompthardening"
              label="System Prompt Hardening"
              isActive={updateSelectedFeatures.prompthardening}
              onChange={(name, value) => setUpdateSelectedFeatures({...updateSelectedFeatures, [name]: value})}
              description="Content moderation and safety controls"
            />
          </div>
        </div>

        <div className="flex justify-center pt-10">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className={`group relative overflow-hidden ${
              isLoading 
                ? 'bg-blue-800/30 cursor-not-allowed scale-95 border border-blue-700/40' 
                : 'bg-blue-600/10 border border-blue-500/30 hover:bg-blue-600/20 hover:border-blue-400/50 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/20 active:scale-100'
            } text-blue-300 px-8 py-4 rounded-xl font-medium text-lg transition-all duration-300 transform focus:outline-none focus:ring-2 focus:ring-blue-500/40`}
          >
            <span className="relative z-10 flex items-center space-x-3">
              <span className="transform transition-transform group-hover:scale-105">
                {isLoading ? 'Updating Tenant...' : 'Update Tenant'}
              </span>
              {!isLoading ? (
                <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                <div className="relative">
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}
            </span>
          </button>
        </div>
      </>
    )}
  </div>
);


  const handleUpdateTabClick = async () => {
    setActiveTab('update');
    setIsLoadingTenants(true);
    try {
      const response = await API.get(`${process.env.REACT_APP_DJANGO_API_URL}/tenants/`);
      setApplications(response.data.tenant_details);
    } catch (error) {
      console.error('Error fetching update-users:', error);
    } finally {
      setIsLoadingTenants(false);
    }
  };


  return (
    <div className='min-h-screen bg-[#0D0D0E] relative'>
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating Particles */}
        {particles.map(particle => (
      <FloatingParticle
        key={particle.id}
        delay={particle.delay}
        duration={particle.duration}
        initialX={particle.initialX}
        initialY={particle.initialY}
      />
    ))}
    
    {/* Gradient Orbs */}
    <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob" />
    <div className="absolute top-40 right-20 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000" />
    <div className="absolute -bottom-32 left-40 w-72 h-72 bg-blue-600 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000" />
    
    {/* Grid Pattern - Fixed to cover entire screen */}
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/5 to-transparent" 
         style={{
           backgroundImage: `
             radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.1) 0%, transparent 70%),
             radial-gradient(circle at 75% 75%, rgba(59, 130, 246, 0.1) 0%, transparent 70%)
           `,
           backgroundSize: '100% 100%', // Ensure full coverage
           backgroundRepeat: 'no-repeat'
         }} />
  </div>

      <div className='pt-[5vh] flex justify-center items-center relative z-10 pb-20'>
        <div className={`w-full max-w-5xl space-y-8 bg-black/60 backdrop-blur-xl border border-[#232325] p-12 rounded-3xl shadow-2xl transform transition-all duration-1000 ${
          mounted ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-10 opacity-0 scale-95'
        }`}>
          
          {/* Header Section */}
          <div className="text-center space-y-6 mb-10">
            <div className="relative group">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 rounded-3xl mx-auto flex items-center justify-center mb-8 transform transition-all duration-500 hover:scale-110 hover:rotate-3 shadow-lg hover:shadow-2xl hover:shadow-blue-500/25">
                <svg className="w-10 h-10 text-white transform transition-transform duration-300 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m0 0h4M9 7h6m-6 4h6m-2 8v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4h4z" />
                </svg>
              </div>
            </div>
            
            <div className="space-y-4">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-white via-blue-200 to-purple-200 bg-clip-text text-transparent animate-gradient-x">
                User Management Portal
              </h2>
              <p className="text-gray-300 text-lg font-medium opacity-80 transform transition-all duration-700 hover:opacity-100">
                Create new applications or manage existing user accounts
              </p>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center space-x-6 mb-12">
            <TabButton
              active={activeTab === 'create'}
              onClick={() => setActiveTab('create')}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              }
            >
              Onboard Tenant
            </TabButton>
            <TabButton
              active={activeTab === 'update'}
              onClick={handleUpdateTabClick}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              }
            >
              Update Tenant
            </TabButton>
          </div>

          {/* Message Display */}
          {message.text && (
            <div className={`p-6 rounded-2xl text-center transform transition-all duration-500 animate-slideInDown ${
              message.type === 'success' 
                ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 shadow-lg shadow-emerald-500/20' 
                : 'bg-red-500/20 border border-red-500/40 text-red-300 shadow-lg shadow-red-500/20'
            }`}>
              <div className="flex items-center justify-center space-x-2">
                {message.type === 'success' ? (
                  <svg className="w-5 h-5 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                <span className="font-medium">{message.text}</span>
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div className="relative">
            <div className={`transition-all duration-500 ${
              activeTab === 'create' ? 'opacity-100 transform translate-x-0' : 'opacity-0 transform translate-x-4 absolute inset-0 pointer-events-none'
            }`}>
              {renderCreateTab()}
            </div>
            
            <div className={`transition-all duration-500 ${
              activeTab === 'update' ? 'opacity-100 transform translate-x-0' : 'opacity-0 transform translate-x-4 absolute inset-0 pointer-events-none'
            }`}>
              {renderUpdateTab()}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes slideInDown {
          from {
            opacity: 0;
            transform: translateY(-30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes gradient-x {
          0%, 100% { background-size: 200% 200%; background-position: left center; }
          50% { background-size: 200% 200%; background-position: right center; }
        }
        
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
        .animate-gradient-x { animation: gradient-x 3s ease infinite; }
        .animate-slideInDown { animation: slideInDown 0.5s ease-out; }
        .hover\\:scale-102:hover { transform: scale(1.02); }
      `}</style>
    </div>
  );
};

export default Onboarding;
